import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { createApiClient } from "../api/client";
import {
  labeebAuditListPath,
  labeebEventsOf,
  type LabeebAuditDomain,
  type LabeebAuditPage,
} from "../api/labeeb";
import {
  auditListPath,
  eventsOf,
  type AuditDomain,
  type AuditPage,
} from "../api/mcp";
import type { AppConfig } from "../config";
import { classifyError, type Resource } from "./useResource";

/**
 * Each product keeps its OWN audit table. This picks which one is being read.
 *
 * The two are structurally identical — same row shape, same opaque cursor,
 * same nullable actor and timestamp — so one hook serves both. What differs is
 * the path and the domain vocabulary, and both of those come from the
 * product's own module rather than being guessed here.
 */
export type AuditProduct = "mcp" | "labeeb";

/** The domain filter, whichever product's vocabulary it is drawn from. */
export type AnyAuditDomain = AuditDomain | LabeebAuditDomain | "";

/**
 * A row from EITHER product's audit table.
 *
 * `domain` widens to `string` here on purpose. MCP types it as its own union
 * (`tenant | quota | settings`) and Labeeb's is a different set
 * (`tenant | quota | flags | config`) — so a row type that kept either union
 * would make the other product's rows a compile error, and casting between
 * them would be a lie the renderer then has to survive. Everything a shared
 * renderer actually needs is common to both, and the parts that are not —
 * where a target links to — stay behind a product check at the call site.
 */
export interface AnyAuditEvent {
  domain: string;
  action: string;
  actor: string | null;
  target: unknown;
  before: unknown;
  after: unknown;
  timestamp: string | null;
  /**
   * Why the operator said they made the change. Common to both products —
   * MCP stores it when the write sent one, Labeeb requires it on every
   * change — so the shared renderer can show it.
   *
   * Optional: old rows predate the field, and a few MCP writes take no
   * reason. Absent is rendered as a dash, never as an empty cell.
   */
  reason?: string | null;
}

export interface AnyAuditPage {
  events?: AnyAuditEvent[];
  count?: number;
  /** Opaque, and NULLABLE — MCP sends an explicit null for "no more pages". */
  cursor?: string | null;
}

/**
 * The fleet audit list, including `?cursor=` for the next page.
 *
 * `useResource` replaces its payload on every path change, so putting the
 * cursor in the path would throw away the rows already on screen. This hook
 * appends. The cursor itself is opaque — it is sent back exactly as received.
 */
export function useAuditFeed(
  config: AppConfig,
  domain: AnyAuditDomain,
  product: AuditProduct = "mcp",
) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [state, setState] = useState<Resource<AnyAuditPage>>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });
      // Each product builds its own path: the day-window rule differs (Labeeb's
      // domain routes carry no date filter), and a domain valid for one is a
      // 422 for the other.
      const path =
        product === "labeeb"
          ? labeebAuditListPath(domain as LabeebAuditDomain | "")
          : auditListPath(domain as AuditDomain | "");
      const page = await api.request<AuditPage | LabeebAuditPage>(path, {
        query: cursor ? { cursor } : undefined,
      });
      // Each product's own total helper, so a truncated response degrades
      // through that product's floor rather than a shared guess.
      const rows: AnyAuditEvent[] =
        product === "labeeb"
          ? labeebEventsOf(page as LabeebAuditPage)
          : eventsOf(page as AuditPage);
      setState((prev) => {
        const fresh = { events: rows, cursor: page.cursor, count: page.count };
        if (!append || prev.kind !== "ready") return { kind: "ready", data: fresh };
        return {
          kind: "ready",
          data: {
            events: [...(prev.data.events ?? []), ...rows],
            cursor: page.cursor,
            count: (prev.data.count ?? 0) + (page.count ?? 0),
          },
        };
      });
    },
    [config.apiUrl, token, domain, product],
  );

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    setMoreError(null);
    fetchPage(null, false).catch((err: unknown) => {
      if (active) setState(classifyError(err));
    });
    return () => {
      active = false;
    };
  }, [fetchPage]);

  const reload = useCallback(() => {
    setState({ kind: "loading" });
    setMoreError(null);
    void fetchPage(null, false).catch((err: unknown) => setState(classifyError(err)));
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (state.kind !== "ready" || !state.data.cursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    void fetchPage(state.data.cursor, true)
      .catch((err: unknown) => {
        const classified = classifyError(err);
        const message =
          classified.kind === "signedout"
            ? "Signed out"
            : "error" in classified
              ? classified.error.message
              : "Could not load more rows";
        setMoreError(message);
      })
      .finally(() => setLoadingMore(false));
  }, [fetchPage, loadingMore, state]);

  return { state, reload, loadMore, loadingMore, moreError };
}
